import {
  columnSizingFeature,
  columnVisibilityFeature,
  flexRender,
  tableFeatures,
  useTable,
} from '@tanstack/react-table';
import { isString } from 'lodash';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';

import { COLORS } from '../../helpers/constants/styleguide';

import { Text } from '../typography';
import { hexToRgba } from '../../helpers/utils/common';
import {
  V7_ROW_PROPS,
  adaptCellRenderer,
  toColumnDefs,
  toColumnVisibility,
  toHiddenHeaderIds,
} from './columnAdapter';

// v8 has no layout plugin, so reproduce the sizing useFlexLayout used to emit
const sizeStyle = size => ({
  boxSizing: 'border-box',
  flex: `${size} 0 auto`,
  minWidth: '0px',
  width: `${size}px`,
});

const DefaultRowCellComponent = ({ cell }) => {
  return (
    <Text weight="medium" color="dark" truncate paragraph smallTracking>
      {cell.value}
    </Text>
  );
};

DefaultRowCellComponent.propTypes = {
  cell: PropTypes.shape({ value: PropTypes.node }).isRequired,
};

/**
 * No rest spread: props not listed here are dropped rather than forwarded.
 *
 * @typedef {Object} CardTableProps
 * @property {Array<{ accessor: string | ((row: object) => unknown), id?: string, columns?: unknown[], Header?: string | React.ElementType, Cell?: React.ElementType, width?: number, minWidth?: number, maxWidth?: number, show?: boolean }>} columns
 * @property {unknown[]} data
 * @property {(props: object, meta: object) => object} [getCustomRowProps]
 * @property {number} [tableIndex]
 * @property {string} [tableName]
 * @property {number} [colDefaultWidth]
 * @property {'center' | 'left' | 'right'} [align]
 * @property {boolean} [fitToContent]
 * @property {React.CSSProperties | null} [style]
 */

/** @type {React.FC<CardTableProps>} */
const features = tableFeatures({
  columnSizingFeature,
  columnVisibilityFeature,
});

const CardTable = ({
  columns,
  data,
  getCustomRowProps,
  tableIndex = 0,
  tableName,
  colDefaultWidth = 100,
  align = 'center',
  fitToContent = false,
  style = null,
}) => {
  const columnDefs = useMemo(() => toColumnDefs(columns), [columns]);
  const columnVisibility = useMemo(
    () => toColumnVisibility(columns),
    [columns],
  );
  const hiddenHeaderIds = useMemo(() => toHiddenHeaderIds(columns), [columns]);
  const defaultColumn = useMemo(
    () => ({
      size: colDefaultWidth,
      cell: adaptCellRenderer(DefaultRowCellComponent),
    }),
    [colDefaultWidth],
  );

  const table = useTable({
    features,
    data,
    columns: columnDefs,
    defaultColumn,
    state: { columnVisibility },
  });

  return (
    <Container className="card-table" style={style}>
      <Table role="table" data-private fitToContent={fitToContent}>
        {table.getHeaderGroups().map(headerGroup => (
          <HeaderContainer role="row" key={headerGroup.id}>
            {headerGroup.headers.map(header => {
              const { header: headerDef } = header.column.columnDef;
              return (
                <HeaderCell
                  key={header.id}
                  role="columnheader"
                  colSpan={header.colSpan}
                  align={align}
                  style={{
                    ...sizeStyle(header.getSize()),
                    ...(hiddenHeaderIds.has(header.column.id)
                      ? { display: 'none' }
                      : {}),
                  }}
                >
                  {isString(headerDef) ? (
                    <Text weight="medium" size="extraSmall">
                      {flexRender(headerDef, header.getContext())}
                    </Text>
                  ) : (
                    flexRender(headerDef, header.getContext())
                  )}
                </HeaderCell>
              );
            })}
          </HeaderContainer>
        ))}

        <BodyContainer>
          {table.getRowModel().rows.map((row, index) => {
            const rowId = `${tableName || 'table'}${tableIndex}-row${index}`;
            const rowProps = getCustomRowProps
              ? { ...V7_ROW_PROPS, ...getCustomRowProps(V7_ROW_PROPS, { row }) }
              : V7_ROW_PROPS;

            return (
              <Row {...rowProps} key={row.id} className="table-row" id={rowId}>
                {row.getVisibleCells().map(cell => (
                  <RowCell
                    key={cell.id}
                    role="cell"
                    align={align}
                    style={sizeStyle(cell.column.getSize())}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </RowCell>
                ))}
              </Row>
            );
          })}
        </BodyContainer>
      </Table>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  overflow: scroll;
`;

const Table = styled.div`
  width: ${({ fitToContent }) => (fitToContent ? 'fit-content' : '100%')};
  padding: 0 10px 15px;

  & > div {
    margin-bottom: 8px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  width: 100%;
  height: 48px;
  padding: 16px 16px;
  align-items: center;
  border-radius: 3px;
  background-blend-mode: multiply;
  background-color: ${hexToRgba(COLORS.azure, 0.09)};
`;

const BodyContainer = styled.div`
  & > div {
    margin-bottom: 8px;
  }
`;

const rowInteractiveStyle = css`
  cursor: pointer;

  :hover {
    box-shadow: 0 2px 10px 2px rgba(214, 221, 231, 0.81);
    border: solid 1px #f2f4f7;
    background-color: #ffffff;
  }

  :active {
    box-shadow: 0 2px 10px 2px rgba(214, 221, 231, 0.81);
    border: solid 1px #f2f4f7;
    background-color: #f9fafc;
  }

  :focus {
    box-shadow: 0 0 5px 0 rgba(0, 166, 255, 0.5);
    border: solid 1px #41beff;
    outline: none;
  }
`;

const rowValidStyle = css`
  border-left: solid 6px ${COLORS.orange} !important;
  &.error-focus {
    box-shadow: 0 2px 10px 2px rgba(214, 221, 231, 0.81);
    border: solid 1px #f2f4f7;
    background-color: #ffffff;
  }
`;

const Row = styled.div.attrs(props => ({
  tabIndex: props.onClick ? '0' : '-1',
}))`
  display: inline-flex;
  width: 100%;
  justify-content: center;
  padding: 16px 16px;
  border-radius: 6px;
  border: solid 1px #ebeff4;
  background-color: #ffffff;
  transition: all 0.3s ease;
  outline: none;

  :first-child {
    text-align: left;
  }

  ${({ onClick }) => onClick && rowInteractiveStyle}

  ${({ valid }) => valid === false && rowValidStyle}
`;

/*
 * The use of justify-content in this component it to support our current web-app HeaderCell component
 * when using HeaderCell component align will not work and without it tooltip will break
 *
 * Todo: update tooltip design and HeaderCell to support normal align
 * */
const HeaderCell = styled.div`
  flex-shrink: 1 !important;
  flex-flow: nowrap;
  overflow: hidden;
  padding: 8px 8px;
  text-align: ${({ align }) => align};

  > * {
    justify-content: center;
  }

  :first-child {
    text-align: left;

    > * {
      justify-content: flex-start;
    }
  }

  :last-child {
    text-align: right;

    > * {
      justify-content: flex-end;
    }
  }
`;

const RowCell = styled.div`
  flex-flow: row nowrap;
  flex-shrink: 1 !important;
  flex-basis: auto;
  overflow: hidden;
  padding: 8px 8px;
  text-align: ${({ align }) => align};

  :first-child {
    text-align: left;
  }
  :last-child {
    text-align: right;
  }
`;

CardTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      accessor: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
        .isRequired,
      id: PropTypes.string,
      columns: PropTypes.array,
      Header: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func,
        PropTypes.elementType,
      ]),
      Cell: PropTypes.oneOfType([PropTypes.func, PropTypes.elementType]),
      width: PropTypes.number,
      minWidth: PropTypes.number,
      maxWidth: PropTypes.number,
    }),
  ).isRequired,
  getCustomRowProps: PropTypes.func,
  tableName: PropTypes.string,
  style: PropTypes.object,
  tableIndex: PropTypes.number,
  colDefaultWidth: PropTypes.number,
  fitToContent: PropTypes.bool,
  align: PropTypes.PropTypes.oneOf(['center', 'left', 'right']),
};

export default CardTable;
